/**
 * AI Brain › Recall (amendment 20 — **board 49 is superseded**).
 *
 * The page held nine items; **one of them was a Recall setting.** The rest
 * belonged to other pages, to the recall builder, or were never settings at all —
 * a row with no control, and a row that set the default of a switch nobody can
 * reach. What is left is the human-call deadline, plus signposts to where the
 * moved settings now live.
 */

export interface RecallAgentSettings {
  /**
   * `recall.human_call.deadline_open_days` — **the page's only setting.**
   *
   * Counted in **days the practice is open**, not calendar days: a task created
   * on Friday with five calendar days would give staff three working days.
   */
  deadlineOpenDays: number;
  /** Recall calls sitting in Triage Inbox right now. */
  callsWaiting: number;
}

/** 1–10 (backend spec §3). Anything outside is refused rather than clamped. */
export const DEADLINE_MIN = 1;
export const DEADLINE_MAX = 10;

/** The one validation message the card shows, inline and in red. */
export const DEADLINE_ERROR = `Enter a number from ${String(DEADLINE_MIN)} to ${String(DEADLINE_MAX)}.`;

export function validateDeadline(value: string): number | undefined {
  if (!/^\d+$/.test(value.trim())) return undefined;
  const n = Number(value);
  return n >= DEADLINE_MIN && n <= DEADLINE_MAX ? n : undefined;
}

/**
 * Every recall has exactly one goal, **picked in the builder** (amendment 20 §4).
 * Four, and this list is the vocabulary everywhere a goal is displayed — the
 * `/recall` list, the AI Team Recall table and the report header.
 *
 * `collect_information` is removed: it mapped to no purpose. *Schedule* and
 * *Goodwill* were never goals at all; *Goodwill* becomes **Loyalty**.
 */
export type RecallGoal = 'book_appointment' | 'reactivate_patient' | 'increase_loyalty' | 'clinical_safety';

export const RECALL_GOALS: {
  id: RecallGoal;
  /** What a practice sees. */
  label: string;
  /** What the result column leads with — a loyalty message carries no ask, so it
   * has no booking number to report. */
  resultNoun: string;
}[] = [
  { id: 'book_appointment', label: 'Book', resultNoun: 'booked' },
  { id: 'reactivate_patient', label: 'Reactivate', resultNoun: 'reactivated' },
  // Amendment 20 §4 called this `Loyalty`; amendments 21 §2.2 and 22 §3 both say
  // `Increase loyalty`, and the newer two agree — so one label, here, rather than
  // a second map on the list page that can drift from this one.
  { id: 'increase_loyalty', label: 'Increase loyalty', resultNoun: 'reached' },
  { id: 'clinical_safety', label: 'Clinical safety', resultNoun: 'responded' },
];

export const RECALL_GOAL_LABEL: Record<RecallGoal, string> = RECALL_GOALS.reduce(
  (acc, g) => ({ ...acc, [g.id]: g.label }),
  {} as Record<RecallGoal, string>,
);

export const RECALL_GOAL_RESULT: Record<RecallGoal, string> = RECALL_GOALS.reduce(
  (acc, g) => ({ ...acc, [g.id]: g.resultNoun }),
  {} as Record<RecallGoal, string>,
);

/** `31% booked`, or an em dash where a recall has never run. */
export function goalResult(goal: RecallGoal, percent: number | undefined): string {
  const noun = RECALL_GOAL_RESULT[goal];
  return percent === undefined ? `— ${noun}` : `${String(percent)}% ${noun}`;
}
